// Pure presentational request-state primitives (doctrine rule 2 / Module 01 §4:
// every screen has explicit loading = skeleton, error = retry card with request
// id, empty = one-line + CTA). No data fetching here, so they are trivially
// unit-testable and shared across the office and floor-pwa areas.

import type { CSSProperties } from "react";

export function LoadingState({ label }: { label: string }) {
  return (
    <p className="state state-loading" role="status">
      {label}
    </p>
  );
}

/** A single shimmering placeholder block; compose these into screen skeletons. */
export function Skeleton({
  width,
  height,
}: {
  width?: string | undefined;
  height?: string | undefined;
}) {
  const style: CSSProperties = {};
  if (width !== undefined) style.width = width;
  if (height !== undefined) style.height = height;
  return <span className="skeleton" style={style} aria-hidden="true" />;
}

/**
 * §4 loading state for tabular screens (S1, S5, S7): skeleton rows. `label` is
 * announced to assistive tech while the visual rows shimmer.
 */
export function TableSkeleton({
  rows = 6,
  cols = 4,
  label,
}: {
  rows?: number;
  cols?: number;
  label: string;
}) {
  return (
    <div className="state-skeleton" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, r) => (
        <div className="skeleton-row" key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} height="1rem" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** §4 loading state for detail/form screens (S2, S3, S4): skeleton card. */
export function CardSkeleton({ lines = 4, label }: { lines?: number; label: string }) {
  return (
    <div className="state-skeleton ds-card" role="status" aria-label={label}>
      <Skeleton width="40%" height="1.4rem" />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height="1rem" />
      ))}
    </div>
  );
}

/**
 * §4 error state: a retry card that also surfaces the request id so a user can
 * quote it to support and it can be traced in the platform logs.
 */
export function ErrorState({
  title,
  detail,
  retryLabel,
  onRetry,
  requestId,
  requestIdLabel,
}: {
  title: string;
  detail?: string | undefined;
  retryLabel: string;
  onRetry: () => void;
  requestId?: string | undefined;
  requestIdLabel?: string | undefined;
}) {
  return (
    <div className="state state-error" role="alert">
      <p className="state-title">{title}</p>
      {detail !== undefined && detail.length > 0 ? <p className="state-detail">{detail}</p> : null}
      {requestId !== undefined && requestId.length > 0 ? (
        <p className="state-request-id">
          {requestIdLabel !== undefined ? `${requestIdLabel}: ` : null}
          <code>{requestId}</code>
        </p>
      ) : null}
      <button type="button" onClick={onRetry}>
        {retryLabel}
      </button>
    </div>
  );
}

/**
 * §4 REFUSED state — the server said no, and it will say no again.
 *
 * Deliberately NOT an `ErrorState`: an error card offers Retry, and a Retry
 * that can never succeed teaches the user that the platform is flaky when in
 * fact it is working exactly as configured. A refusal is a stable fact about
 * this person's role, so it is stated once, with no control.
 *
 * Measured cost of not having it (surface review, 2026-08-13): a storekeeper
 * opening the waste ledger saw eight seconds of skeleton, then "Could not load
 * the waste ledger · Request ID · Retry".
 */
export function ForbiddenState({ title, body }: { title: string; body: string }) {
  return (
    <div className="state state-forbidden" role="status" data-testid="state-forbidden">
      <p className="state-title">{title}</p>
      <p className="state-body">{body}</p>
    </div>
  );
}

/**
 * §4 OFFLINE state — the request is PAUSED, not slow.
 *
 * react-query pauses a query when the browser reports no network and resumes it
 * on reconnect. It never times out, so a screen that only knows "pending" shows
 * a loading skeleton forever and says nothing true: measured at 2 s, 5 s, 10 s
 * and 20 s with the network cut, the waste ledger showed an indefinite skeleton
 * and no banner. The pause is a first-class signal (`fetchStatus === "paused"`)
 * — say what it means and promise the resume, which does happen (rows returned
 * within 3 s of reconnect).
 */
export function OfflineState({ title, body }: { title: string; body: string }) {
  return (
    <div className="state state-offline" role="status" data-testid="state-offline">
      <p className="state-title">{title}</p>
      <p className="state-body">{body}</p>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  ctaLabel,
  onCta,
}: {
  title: string;
  body: string;
  ctaLabel?: string | undefined;
  onCta?: (() => void) | undefined;
}) {
  return (
    <div className="state state-empty">
      <p className="state-title">{title}</p>
      <p className="state-body">{body}</p>
      {ctaLabel !== undefined && onCta !== undefined ? (
        <button type="button" onClick={onCta}>
          {ctaLabel}
        </button>
      ) : null}
    </div>
  );
}
